import React, { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, NavLink } from 'react-router-dom';
import 'bootstrap/dist/css/bootstrap.min.css';
import 'bootstrap/dist/js/bootstrap.bundle.min.js';
import './pages.css';
import render_blogs from '../blog/routing';

// Lazy Loading for Performance Optimization
const Home = lazy(() => import('./Home'));
const Hardware = lazy(() => import('./hardware'));
const Cloud = lazy(() => import('./cloud_applications'));
const Emailjs = lazy(() => import('./email/emailjs'));
const TechLinks = lazy(() => import('./tech_cards'));
const DbmWatt = lazy(() => import('./calculators/dbm_to_watt'));
const GainConverter = lazy(() => import('./calculators/dB'));
const HexDecimalBinaryConverter = lazy(() => import('./calculators/hex-dec-binary'));
const Trace = lazy(() => import('./calculators/trace'));
const DevelopmentSelection = lazy(() => import('./cost/estimate'));
const LteTechnologySelection = lazy(() => import('./lte_tech'));
const DeviceRecommendation = lazy(() => import('./device/device_development'));
const EE101 = lazy(() => import('./EE101'));

const PLMNSelection = lazy(() => import('./lte_commands/at_cops'));
const OperatorNames = lazy(() => import('./lte_commands/at_copn'));
const EPSNetworkRegistration = lazy(() => import('./lte_commands/at_cereg'));
const PhoneFunctionality = lazy(() => import('./lte_commands/at_cfun'));
const PDPContext = lazy(() => import('./lte_commands/at_cgact'));
const PSAttachDetach = lazy(() => import('./lte_commands/at_cgatt'));
const PreferredPLMNList = lazy(() => import('./lte_commands/at_cpol'));
const SignalQuality = lazy(() => import('./lte_commands/at_csq'));
const CGDCONTPage = lazy(() => import('./lte_commands/AT_CGDCONT_'));
const CGCMOD = lazy(() => import('./lte_commands/AT_CGCMOD'));


const LTEConnectionAlgorithm = lazy(() => import('./lte/LTEConnectionAlgorithm'));
const NetworkSelectionPage = lazy(() => import('./lte/NetworkSelectionPage'));
const PPPFrames = lazy(() => import('./lte/PPPFrames'));
const PostDemo = lazy(() => import('./PostDemo'));

const DropdownMenu = ({ title, items }) => (
  <li className="nav-item dropdown">
    <NavLink className="nav-link dropdown-toggle text-dark fw-bold" to="#" role="button" data-bs-toggle="dropdown">
      {title}
    </NavLink>
    <ul className="dropdown-menu dropdown-menu-end shadow-sm rounded-3">
      {items.map(({ label, path }) => (
        <li key={path}>
          <NavLink className="dropdown-item" to={path}>{label}</NavLink>
        </li>
      ))}
    </ul>
  </li>
);

const NavigationBar = () => (
  <BrowserRouter>
    <nav className="navbar navbar-expand-lg navbar-light bg-gradient shadow-sm py-2" style={{ backgroundColor: 'rgba(255, 255, 255, 0.5)' }}>
      <div className="container">
      <NavLink className="navbar-brand fw-bold text-primary fs-4" to="/">
            JINet Innovations
            <img
                src="https://upload.wikimedia.org/wikipedia/en/a/a4/Flag_of_the_United_States.svg"
                alt="USA Flag"
                width="85"
                style={{ marginRight: "5px", marginLeft: "20px" }} // Corrected JSX syntax
                />
            <img
                src="https://upload.wikimedia.org/wikipedia/commons/0/01/Flag_of_California.svg"
                alt="California Flag"
                width="75"
                style={{ marginLeft: "10px" }} // Corrected JSX syntax
            />
        </NavLink>

        <button className="navbar-toggler" type="button" data-bs-toggle="collapse" data-bs-target="#navbarContent">
          <span className="navbar-toggler-icon"></span>
        </button>
        <div className="collapse navbar-collapse" id="navbarContent">
          <ul className="navbar-nav ms-auto align-items-left gap-3">
            <li className="nav-item"><NavLink className="nav-link text-dark fw-bold" to="/">Home</NavLink></li>
            <DropdownMenu
              title="Services"
              items={[
                { label: 'Hardware Design', path: '/hardware' },
                { label: 'Software Design', path: '/cloud_applications' },
                { label: 'LTE Technology Selector', path: '/lte_tech' },
                { label: 'Project Cost Estimate', path: '/cost/estimate' },
                { label: 'Device Development', path: '/device_development' },
              ]}
            />
            <li className="nav-item"><NavLink className="nav-link text-dark fw-bold" to="/tech_cards">Projects</NavLink></li>
            <li className="nav-item"><a className="nav-link text-dark fw-bold" href="https://groups.google.com/g/jinetinnovations" target="_blank" rel="noopener noreferrer">Forum</a></li>
            <li className="nav-item"><NavLink className="nav-link text-dark fw-bold" to="/email/emailjs">Contact</NavLink></li>
            <DropdownMenu
              title="Calculators"
              items={[
                { label: 'dBm to Watt', path: '/calculators/dbm_to_watt' },
                { label: 'Gain Converter', path: '/calculators/dB' },
                { label: 'Hex-Dec-Binary', path: '/calculators/hex-dec-binary' },
                { label: 'Trace Parameters', path: '/calculators/trace' },
              ]}
            />
            <DropdownMenu
              title="AT+ Commands"
              items={[
                { label: 'AT+COPS', path: '/lte_commands/at_cops' },
                { label: 'AT+COPN', path: '/lte_commands/at_copn' },
                { label: 'AT+CEREG', path: '/lte_commands/at_cereg' },
                { label: 'AT+CFUN', path: '/lte_commands/at_cfun' },
                { label: 'AT+CGACT', path: '/lte_commands/at_cgact' },
                { label: 'AT+CGATT', path: '/lte_commands/at_cgatt' },
                { label: 'AT+CPOL', path: '/lte_commands/at_cpol' },
                { label: 'AT+CCQ', path: '/lte_commands/at_csq' },
                { label: 'AT+CGDCONT', path: '/lte_commands/AT_CGDCONT_' },
                { label: 'AT+CGCMOD', path: '/lte_commands/AT_CGCMOD' },
              ]}
            />
            <DropdownMenu
              title="LTE"
              items={[
                { label: 'LTE Connection Algorithm', path: '/lte/LTEConnectionAlgorithm'},
                { label: 'Network Selection with a Multi-Carrier SIM', path: '/lte/NetworkSelectionPage'},
                { label: 'PPP Frames', path: '/lte/PPPFrames'},
              ]}
            />
            <li className="nav-item"><NavLink className="nav-link text-dark fw-bold" to="/blog">Blog</NavLink></li>
            <li className="nav-item"><NavLink className="nav-link text-dark fw-bold" to="/EE101">EE101</NavLink></li>
            <li className="nav-item"><NavLink className="nav-link text-dark fw-bold" to="/PostDemo">Dev</NavLink></li>
          </ul>
        </div>
      </div>
    </nav>

    <Suspense fallback={<div>Loading...</div>}>
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/hardware" element={<Hardware />} />
        <Route path="/cloud_applications" element={<Cloud />} />
        <Route path="/email/emailjs" element={<Emailjs />} />
        <Route path="/tech_cards" element={<TechLinks />} />
        <Route path="/calculators/dbm_to_watt" element={<DbmWatt />} />
        <Route path="/calculators/dB" element={<GainConverter />} />
        <Route path="/calculators/hex-dec-binary" element={<HexDecimalBinaryConverter />} />
        <Route path="/calculators/trace" element={<Trace />} />
        <Route path="/cost/estimate" element={<DevelopmentSelection />} />
        <Route path="/lte_tech" element={<LteTechnologySelection />} />
        <Route path="/device_development" element={<DeviceRecommendation />} />
        {render_blogs()}
        <Route path="/EE101" element={<EE101 />} />
        <Route path="/lte_commands/at_cops" element={<PLMNSelection />} />
        <Route path="/lte_commands/at_copn" element={<OperatorNames />} />
        <Route path="/lte_commands/at_cereg" element={<EPSNetworkRegistration />} />
        <Route path="/lte_commands/at_cfun" element={<PhoneFunctionality />} />
        <Route path="/lte_commands/at_cgact" element={<PDPContext />} />
        <Route path="/lte_commands/at_cgatt" element={<PSAttachDetach />} />
        <Route path="/lte_commands/at_cpol" element={<PreferredPLMNList />} />
        <Route path="/lte_commands/at_csq" element={<SignalQuality />} />
        <Route path="/lte_commands/AT_CGDCONT_" element={<CGDCONTPage />} />
        <Route path="/lte_commands/AT_CGCMOD" element={<CGCMOD />} />

        <Route path="/lte/LTEConnectionAlgorithm" element={<LTEConnectionAlgorithm />} />
        <Route path="/lte/NetworkSelectionPage" element={<NetworkSelectionPage />} />
        <Route path="/lte/PPPFrames" element={<PPPFrames />} />
        <Route path="/PostDemo" element={<PostDemo />} />
      </Routes>
    </Suspense>
  </BrowserRouter>
);

export default NavigationBar;
